import { c as _c } from "react/compiler-runtime";
import { CreditCardCheckIcon, CreditCardRecurringPaymentIcon, CreditCardUpIcon, CreditCardXIcon, ShoppingBag02Icon, UserPlus01Icon } from '@systemeio/icons';
import { centsToUnits } from '@systemeio/util';
import LazyContactInfoTooltip from 'modules/contacts/components/lazy-contact-info-tooltip';
import { getFunnelStepUrl } from 'modules/funnels/funnel/utils/get-funnel-step-url';
import { mainEventsDescription, MainEventsEnum, mainEventsType } from 'modules/main/enums/main-events-enum';
import { EventChargeInterface, EventPaymentInterface, MainEventType } from 'modules/main/types/main-interface';
import React, { type JSX } from 'react';
import { IconTile } from 'shared/components/icon-tile';
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch';
import { RelativeTime } from 'shared/components/relative-time';
import TransLoco from 'shared/components/trans-loco';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import useUser from 'shared/hooks/use-user';
import { currencyFormat } from 'shared/utils/currency-format';
import { getBlogPostNextjsUrl } from 'shared/utils/get-blog-post-nextjs-url';
interface RenderEventProps {
  event: MainEventType;
}
const eventIcon: Record<MainEventsEnum, JSX.Element> = {
  [MainEventsEnum.contact_created]: <IconTile size={'s'} aria-hidden="true" tone="lightGreen">
      <UserPlus01Icon />
    </IconTile>,
  [MainEventsEnum.new_signup]: <IconTile size={'s'} aria-hidden="true" tone="lightGreen">
      <UserPlus01Icon />
    </IconTile>,
  [MainEventsEnum.recurring_charge_succeed]: <IconTile size={'s'} aria-hidden="true" tone="blue">
      <CreditCardRecurringPaymentIcon />
    </IconTile>,
  [MainEventsEnum.one_shot_charge_succeed]: <IconTile size={'s'} aria-hidden="true" tone="lightGreen">
      <ShoppingBag02Icon />
    </IconTile>,
  [MainEventsEnum.payment_subscription_stopped]: <IconTile size={'s'} aria-hidden="true" tone="gray">
      <CreditCardXIcon />
    </IconTile>,
  [MainEventsEnum.payment_subscription_completed]: <IconTile size={'s'} aria-hidden="true" tone="blue">
      <CreditCardCheckIcon />
    </IconTile>,
  [MainEventsEnum.payment_subscription_created]: <IconTile size={'s'} aria-hidden="true" tone="blue">
      <CreditCardUpIcon />
    </IconTile>,
  [MainEventsEnum.recurring_charge_failed]: <IconTile size={'s'} aria-hidden="true" tone="danger">
      <CreditCardXIcon />
    </IconTile>
};
function RenderEvent(t0) {
  const $ = _c(24);
  const {
    event
  } = t0;
  const {
    t
  } = useLocoTranslation();
  const {
    user
  } = useUser();
  const getOfferName = _temp;
  const getCustomerName = _temp2;
  const createContactTooltip = _temp3;
  const createFunnelStepLink = _temp4;
  let t1;
  if ($[0] !== event || $[1] !== t || $[2] !== user?.dashboardLocale) {
    t1 = () => {
      switch (event.type) {
        case MainEventsEnum.new_signup:
          {
            const {
              relatedObject: relatedObject_1
            } = event;
            return <TransLoco t={t} i18nKey={mainEventsDescription[event.type]} values={{
              email: relatedObject_1.email
            }} components={{
              contactLink: createContactTooltip(relatedObject_1.id, relatedObject_1.removedAt)
            }} />;
          }
        case MainEventsEnum.recurring_charge_succeed:
        case MainEventsEnum.one_shot_charge_succeed:
          {
            const {
              relatedObject: relatedObject_0
            } = event;
            const {
              orderItem: orderItem_1,
              payment,
              amount
            } = relatedObject_0;
            const {
              customer: customer_1
            } = orderItem_1.order;
            const contact_0 = customer_1.contact;
            return <TransLoco t={t} i18nKey={mainEventsDescription[event.type]} values={{
              price: currencyFormat(centsToUnits(amount), payment.currency, user?.dashboardLocale),
              offer_name: getOfferName(orderItem_1),
              customer_name: getCustomerName(customer_1)
            }} components={{
              contactLink: createContactTooltip(contact_0.id, contact_0.removedAt),
              a: createFunnelStepLink(orderItem_1.offer)
            }} />;
          }
        case MainEventsEnum.payment_subscription_created:
        case MainEventsEnum.recurring_charge_failed:
        case MainEventsEnum.payment_subscription_stopped:
        case MainEventsEnum.payment_subscription_completed:
          {
            const {
              orderItem: orderItem_0
            } = event.relatedObject;
            const {
              customer: customer_0
            } = orderItem_0.order;
            const contact = customer_0.contact;
            return <TransLoco t={t} i18nKey={mainEventsDescription[event.type]} values={{
              customer_name: getCustomerName(customer_0),
              offer_name: getOfferName(orderItem_0)
            }} components={{
              a: createContactTooltip(contact.id, contact.removedAt),
              b: createFunnelStepLink(orderItem_0.offer)
            }} />;
          }
        case MainEventsEnum.contact_created:
          {
            const {
              relatedObject
            } = event;
            const {
              sourcePage,
              email,
              id,
              removedAt: removedAt_0,
              pageTitle
            } = relatedObject;
            const contactTooltip = createContactTooltip(id, removedAt_0);
            if (!sourcePage) {
              return <TransLoco t={t} i18nKey="dashboard.main.event.message.contact_created_without_source_page" values={{
                email
              }} components={{
                contactLink: contactTooltip
              }} />;
            }
            const getHref = () => {
              if (sourcePage.funnelStep) {
                return getFunnelStepUrl(sourcePage.funnelStep.funnel.id, sourcePage.funnelStep.id);
              }
              if (sourcePage.blogPage) {
                return getBlogPostNextjsUrl(sourcePage.blogPage.blog.id);
              }
              return "";
            };
            const sourcePageLink = <LinkWithoutPrefetch href={getHref()} className="text-text-primary hover:underline" />;
            return <TransLoco t={t} i18nKey={mainEventsDescription[event.type]} values={{
              email,
              form: pageTitle
            }} components={{
              a: contactTooltip,
              b: sourcePageLink
            }} />;
          }
      }
    };
    $[0] = event;
    $[1] = t;
    $[2] = user?.dashboardLocale;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const getEventElement = t1;
  const t2 = eventIcon[event.type];
  const t3 = mainEventsType[event.type];
  let t4;
  if ($[4] !== t || $[5] !== t3) {
    t4 = t(t3);
    $[4] = t;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t4) {
    t5 = <span className="font-medium">{t4}</span>;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== event.createdAt) {
    t6 = <span className="text-text-tertiary truncate text-sm"><RelativeTime time={event.createdAt} /></span>;
    $[9] = event.createdAt;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t5 || $[12] !== t6) {
    t7 = <div className="flex items-center justify-between gap-3">{t5}{t6}</div>;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== getEventElement) {
    t8 = getEventElement();
    $[14] = getEventElement;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t8) {
    t9 = <p className="text-text-secondary wrap-anywhere">{t8}</p>;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t7 || $[19] !== t9) {
    t10 = <div className="flex min-w-0 flex-1 flex-col">{t7}{t9}</div>;
    $[18] = t7;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t10 || $[22] !== t2) {
    t11 = <div className="flex gap-3">{t2}{t10}</div>;
    $[21] = t10;
    $[22] = t2;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  return t11;
}
function _temp4(offer) {
  if (!offer?.funnelStep) {
    return <span />;
  }
  return <LinkWithoutPrefetch href={getFunnelStepUrl(offer.funnelStep.funnel.id, offer.funnelStep.id)} className="text-text-primary hover:underline" />;
}
function _temp3(contactId, removedAt) {
  return <LazyContactInfoTooltip contactId={contactId} removedAt={removedAt} />;
}
function _temp2(customer) {
  return customer.fullName || customer.contact.connectedUser?.fullName || customer.contact.email;
}
function _temp(orderItem) {
  if (orderItem.pricePlan) {
    return orderItem.pricePlan.innerName;
  }
  if (orderItem.productOrderItem) {
    return orderItem.productOrderItem.product.name;
  }
  return "";
}
export default RenderEvent;